<template>
  <section>
    <div v-if="blockUser">
      <div class="text-center">
        <h1>Unauthorized Customer</h1>
        <br />
        <p>
          Sorry, this user ({{ user.email }}) is not authorized to view this
          sizing.
        </p>
      </div>
    </div>
    <UILoadingScreen
      v-else-if="!dataLoaded"
      :loading="true"
      loadingMessage="Loading Sizing..."
    />
    <div v-else-if="sizingsQuery.data.value?.sizings.length === 0">
      <div class="text-center">
        <h1>Sizing Not Found</h1>
        <p>
          Sorry, the sizing with the id "{{ route.params.sizingId }}" couldn't
          be found.
        </p>
        <p class="mt-4">Click here to go back to the sizings list</p>
        <router-link :to="{ name: 'Sizings' }">All Sizings</router-link>
      </div>
    </div>
    <section
      v-else-if="!route.params.scenarioId && route.name !== 'Reports'"
      class="scenario-loader"
    >
      <Dropdown
        :modelValue="selectedScenario"
        :options="scenarioSwitcherOptions"
        class="w-full"
        optionValue="id"
        append-to="body"
        @change="onScenarioDropdownChange"
      >
        <template #option="{ option: { title, id } }">
          {{ title }}
          <Badge
            v-if="id === sizing?.installed_scenario_id"
            value="Installed"
            class="p-badge-installed ml-2"
          />
        </template>
        <template #value="{ value }">
          <div v-if="value" class="flex align-items-center">
            {{ scenarioTitle(value) }}
            <Badge
              v-if="value === sizing?.installed_scenario_id"
              class="p-badge-installed ml-2"
              value="Installed"
            />
          </div>
          <!-- placeholder text -->
          <div v-else>Select a Scenario</div>
        </template>
      </Dropdown>
    </section>
    <SizingsDetail
      v-else
      :isFetchingSizing="sizingsQuery.fetching.value"
      :sizing="sizing as Sizing"
      :sizingId="String(route.params.sizingId)"
      :scenarioId="String(route.params.scenarioId)"
      :perfevalId="String(route.params.perfevalId)"
      :routeName="route.name"
      :userCustomers="userCustomersQuery.data.value?.customers || []"
    />
  </section>
</template>

<script setup lang="ts">
import { useCustomersQuery, useSizingsQuery } from "gql/api";
import { useRouter, type RouteRecordName, useRoute } from "vue-router";
import { computed, ref, watch } from "vue";
import type { SpyglassUser } from "@/composables/auth/useUserInfo";
import useUserInfo from "@/composables/auth/useUserInfo";
import SizingsDetail from "./SizingsDetail.vue";
import type { Scenario, Sizing } from "@/composables/forms/formTypes";
import type { DropdownChangeEvent } from "primevue/dropdown";
import {
  getInstalledOrLatestScenarioId,
  orderScenariosBySizingOrder
} from "@/utils/scenarioUtils";
import UILoadingScreen from "@/components/UILoadingScreen.vue";

const props = defineProps<{
  sizingId: string | null;
  scenarioId: string | null;
  perfevalId: string | null;
  routeName: RouteRecordName | null | undefined;
}>();

const route = useRoute();
const router = useRouter();

const user = useUserInfo() as SpyglassUser;
const blockUser = ref(false);
const erpCustomers = user.customers as string[];

const isNewSizing = computed(() => props.sizingId === "new");

const sizing = computed(
  () => sizingsQuery.data.value?.sizings[0] as unknown as Sizing
);
const selectedScenario = ref();

const userCustomersQuery = useCustomersQuery({
  variables: computed(() => ({
    where: {
      customer_erp_id: {
        _in: user.customers
      }
    }
  }))
});

const sizingsQuery = useSizingsQuery({
  variables: computed(() => ({
    withAggregate: false,
    kitchenSink: true,
    where: {
      id: { _eq: route.params.sizingId }
    }
  })),
  // This query must use the network-only policys
  // because the data returned is used to initialize the form refs
  // and cannot be stale cache data
  requestPolicy: "network-only",
  pause: isNewSizing
});

const scenarios = computed(
  () => sizingsQuery.data.value?.sizings[0]?.scenarios || []
);

const scenarioSwitcherOptions = computed(() => {
  const options = [
    { title: "Create New Scenario...", id: "new" },
    ...orderScenariosBySizingOrder(sizing.value, scenarios.value ?? [])
  ];
  return options;
});

const onScenarioDropdownChange = async (event: DropdownChangeEvent) => {
  await router.push({
    name: "ScenarioInfo",
    params: {
      sizingId: sizingsQuery.data.value?.sizings[0].id,
      scenarioId: event.value
    }
  });
};

function scenarioTitle(match: string) {
  if (scenarioSwitcherOptions.value?.length) {
    return scenarioSwitcherOptions.value.find(s => s.id === match)?.title;
  }
  return "";
}

const dataLoaded = computed(() => {
  if (isNewSizing.value) {
    return true;
  } else if (props.sizingId !== route.params.sizingId) {
    return false;
  } else {
    return (
      !(userCustomersQuery.fetching.value && sizingsQuery.fetching.value) &&
      !!sizingsQuery.data.value?.sizings &&
      !!userCustomersQuery.data.value?.customers
    );
  }
});

const customerGuard = () => {
  if (route.params.sizingId === "new") return;

  if (user.is_customer) {
    // block if user is customer and sizing imported from legacy
    if (sizing.value.journal_author_id === "import-user") {
      blockUser.value = true;
    }
    // if user is customer, check that sizing belongs to one of their customers
    else if (sizing.value?.customer) {
      if (
        !erpCustomers.includes(sizing.value.customer.customer_erp_id) ||
        !sizing.value.share_with_customer
      ) {
        blockUser.value = true;
      }
    } else if (sizing.value?.author?.id !== user.id) {
      blockUser.value = true;
    }
  }
};

const verifyScenario = async () => {
  if (props.scenarioId === "new") return;

  const scenarioIds = sizing.value?.scenarios?.map(
    (scenario: Scenario) => scenario.id
  );
  if (!scenarioIds?.includes(props.scenarioId)) {
    await router.replace({
      params: {
        sizingId: route.params.sizingId,
        scenarioId:
          getInstalledOrLatestScenarioId(sizing.value, scenarios.value) || "new"
      }
    });
  }
};

const handleRedirect = async () => {
  if (route.query.deletedScenario) {
    const nextScenarioId =
      getInstalledOrLatestScenarioId(sizing.value, scenarios.value) || "new";
    return router.replace({
      name: "ScenarioInfo",
      params: {
        sizingId: route.params.sizingId,
        scenarioId: nextScenarioId
      }
    });
  }

  /* eslint-disable no-fallthrough */
  if (!dataLoaded.value) return;
  if (props.sizingId !== route.params.sizingId) return;

  customerGuard();
  await verifyScenario();
};

if (isNewSizing.value) handleRedirect();
watch(
  [
    dataLoaded,
    () => route.params,
    () => route.name,
    () => sizingsQuery.data.value
  ],
  handleRedirect,
  {
    immediate: true // required for offline
  }
);
</script>

<style lang="scss" scoped>
.scenario-loader {
  max-width: 400px;
  margin: 20px auto auto auto;
}
</style>
